import type { VanillaLinkProps } from '../Link/index.ts';
import type { FloatingAlign, FloatingSide } from '../types/floating.ts';
import { Menu as BaseMenu } from '@base-ui/react/menu';
import clsx from 'clsx';
import { createContext, isValidElement, use } from 'react';
import { Button } from '../Button/index.ts';

const MenuDarkContext = createContext(false);

export function Menu({ children, dark = false }: React.PropsWithChildren<{ dark?: boolean }>) {
  return (
    <MenuDarkContext value={dark}>
      <BaseMenu.Root>{children}</BaseMenu.Root>
    </MenuDarkContext>
  );
}

export function MenuTrigger({
  render = <Button />,
  ...props
}: Pick<
  BaseMenu.Trigger.Props,
  'children' | 'className' | 'disabled' | 'nativeButton' | 'render'
>) {
  return <BaseMenu.Trigger {...props} render={render} />;
}

export function MenuPopup({
  align = 'center',
  children,
  className,
  side = 'bottom'
}: React.PropsWithChildren<{
  align?: FloatingAlign;
  className?: string;
  side?: FloatingSide;
}>) {
  const dark = use(MenuDarkContext);

  return (
    <BaseMenu.Portal>
      <BaseMenu.Positioner
        align={align}
        alignOffset={align === 'end' ? 4 : align === 'start' ? -4 : 0}
        className="z-50 outline-none"
        collisionPadding={4}
        side={side}
        sideOffset={8}
      >
        <BaseMenu.Popup
          data-mode={dark ? 'dark' : undefined}
          className={clsx(
            'origin-(--transform-origin) transition-[opacity,scale] duration-100 ease-out data-ending-style:scale-95 data-ending-style:opacity-0 data-starting-style:scale-95 data-starting-style:opacity-0 motion-reduce:transition-none',
            className,

            // Base styles
            'isolate w-max rounded-lg p-1 focus-visible:outline-hidden',

            // Handle scrolling when menu won't fit in viewport
            'max-h-(--available-height) overflow-y-auto overscroll-contain',

            // Popover background
            'bg-white/75 backdrop-blur-lg dark:bg-gray-800/75',

            // Shadows
            'shadow-lg ring-1 ring-gray-950/10 dark:ring-white/10 dark:ring-inset',

            // Define grid at the menu level if subgrid is supported
            'supports-[grid-template-columns:subgrid]:grid supports-[grid-template-columns:subgrid]:grid-cols-[auto_1fr_1.5rem_0.5rem_auto]'
          )}
        >
          {children}
        </BaseMenu.Popup>
      </BaseMenu.Positioner>
    </BaseMenu.Portal>
  );
}

export function MenuHeader(props: React.PropsWithChildren) {
  return <div {...props} className="col-span-5 px-3.5 pt-2.5 pb-1 sm:px-3" />;
}

export function MenuGroup(props: React.PropsWithChildren) {
  return (
    <BaseMenu.Group
      {...props}
      className="col-span-full supports-[grid-template-columns:subgrid]:grid supports-[grid-template-columns:subgrid]:grid-cols-[auto_1fr_1.5rem_0.5rem_auto]"
    />
  );
}

export function MenuGroupLabel(props: React.PropsWithChildren) {
  return (
    <BaseMenu.GroupLabel
      {...props}
      className="col-span-full grid grid-cols-[1fr_auto] gap-x-12 px-3.5 pt-2 pb-1 text-sm/5 font-medium text-gray-500 sm:px-3 sm:text-xs/5"
    />
  );
}

export function MenuSeparator() {
  return (
    <BaseMenu.Separator className="col-span-full mx-3.5 my-1 h-px border-0 bg-gray-950/5 sm:mx-3 dark:bg-white/10" />
  );
}

export function MenuItem({
  children,
  className,
  color = 'default',
  disabled,
  onClick,
  renderLink
}: {
  children: React.ReactNode;
  className?: string;
  color?: 'default' | 'danger';
  disabled?: boolean;
  onClick?: React.MouseEventHandler<HTMLElement>;
  renderLink?: (props: VanillaLinkProps) => React.ReactNode;
}) {
  const classes = clsx(
    className,
    // Base styles
    'group cursor-pointer rounded-md px-3.5 py-2.5 focus:outline-hidden sm:px-3 sm:py-1.5',

    // Text styles
    'text-left text-base/6 sm:text-sm/6',
    color === 'default' && 'text-gray-950 dark:text-white',
    color === 'danger' && 'text-red-600 dark:text-red-400',

    // Focus
    'data-highlighted:bg-gray-950/5 dark:data-highlighted:bg-white/5',

    // Disabled state
    'data-disabled:cursor-default data-disabled:opacity-50',

    // Use subgrid when available but fallback to an explicit grid layout if not
    'col-span-full grid grid-cols-[auto_1fr_1.5rem_0.5rem_auto] items-center supports-[grid-template-columns:subgrid]:grid-cols-subgrid',

    // Icons
    color === 'default' && '*:data-[slot=icon]:text-gray-500',
    color === 'danger' && '*:data-[slot=icon]:text-red-500'
  );

  const link = renderLink?.({ children, className: classes, plain: true });

  if (!disabled && isValidElement(link)) {
    return <BaseMenu.LinkItem closeOnClick render={link} />;
  }

  return (
    <BaseMenu.Item className={classes} disabled={disabled} onClick={onClick}>
      {children}
    </BaseMenu.Item>
  );
}

export function MenuItemLabel({
  className,
  ...props
}: React.PropsWithChildren<{ className?: string }>) {
  return (
    <span
      {...props}
      className={clsx(className, 'col-start-2 row-start-1 text-nowrap')}
      data-slot="label"
    />
  );
}

export function MenuItemDescription({
  className,
  ...props
}: React.PropsWithChildren<{ className?: string }>) {
  return (
    <span
      {...props}
      className={clsx(
        className,
        'col-span-2 col-start-2 row-start-2 text-sm/5 text-gray-500 sm:text-xs/5 dark:text-gray-400'
      )}
      data-slot="description"
    />
  );
}

export function MenuItemAvatar({
  className,
  ...props
}: React.PropsWithChildren<{ className?: string }>) {
  return (
    <div {...props} className={clsx('mr-2.5 -ml-1 size-6 sm:mr-2 sm:size-5')} data-slot="avatar" />
  );
}

export function MenuItemIcon({
  className,
  placement = 'start',
  ...props
}: React.PropsWithChildren<{
  className?: string;
  placement?: 'start' | 'end';
}>) {
  return (
    <div
      {...props}
      className={clsx(
        'row-start-1 shrink-0 *:size-5 sm:*:size-4',
        placement === 'end'
          ? 'col-start-5 -mr-0.5 ml-2.5 sm:ml-2'
          : 'col-start-1 mr-2.5 -ml-0.5 sm:mr-2',
        className
      )}
      data-slot="icon"
    />
  );
}
